import React, { memo, useCallback, useMemo, useState } from 'react';
import ReactECharts from 'echarts-for-react';
import { Button, Card, Form } from 'react-bootstrap';
import { useFormat } from '../../../components/FormatContext';
import { DASHBOARD_CHART_TYPES } from '../utils/dashboardConstants';
import { normalizeSalesChartData } from '../utils/dashboardHelpers';

const SalesChartContainer = ({ data, currencyCode }) => {
    const { formatCurrency, baseFormatters } = useFormat();
    const [isBarChart, setIsBarChart] = useState(false);
    const [selectedChart, setSelectedChart] = useState('sales');

    const chartData = useMemo(() => {
        switch (selectedChart) {
            case 'ordersQty':
                return data?.OrderQtyData || [];
            case 'income':
                return data?.IncomeData || [];
            case 'sales':
            default:
                return data?.SalesData || [];
        }
    }, [data, selectedChart]);

    const processedData = useMemo(() => normalizeSalesChartData(chartData), [chartData]);

    const series = useMemo(() => {
        const colorPalette = ['#8884d8', '#82ca9d', '#8884d8', '#82ca9d'];

        if (isBarChart) {
            const allKeys = Object.keys(chartData?.[0] || {}).filter((key) => key !== 'month');

            return allKeys.map((key, index) => ({
                name: key,
                type: 'bar',
                data: chartData.map((item) => item[key]),
                stack: key.includes('PreviousYear') ? 'previousYear' : 'currentYear',
                itemStyle: { color: colorPalette[index % colorPalette.length] },
            }));
        }
        const yearLabels = data?.yearLabels || {
            previousYear: 'Previous Year',
            currentYear: 'Current Year',
        };

        return [
            {
                name: String(yearLabels.previousYear),
                type: 'line',
                data: processedData.map((item) => item.TotalPreviousYear),
                smooth: true,
                symbol: 'none',
                lineStyle: { color: '#8884d8', width: 2 },
            },
            {
                name: String(yearLabels.currentYear),
                type: 'line',
                data: processedData.map((item) => item.TotalCurrentYear),
                smooth: true,
                symbol: 'none',
                lineStyle: { color: '#82ca9d', width: 2 },
            },
        ];
    }, [chartData, isBarChart, processedData, data?.yearLabels]);

    const option = useMemo(() => {
        const isOrdersQty = selectedChart === 'ordersQty';

        return {
            tooltip: {
                trigger: 'axis',
                formatter: (params) => {
                    const dateStr = params[0]?.name || '';
                    const lines = params.map((item) => {
                        const formattedValue = isOrdersQty
                            ? baseFormatters.number.plain.format(item.value)
                            : formatCurrency(item.value, currencyCode);

                        return `${item.marker} ${item.seriesName}: ${formattedValue}`;
                    });

                    return `${dateStr}<br/>${lines.join('<br/>')}`;
                },
            },
            legend: { data: series.map((s) => s.name), bottom: 0 },
            xAxis: { type: 'category', data: chartData.map((item) => item.month) },
            yAxis: {
                type: 'value',
                axisLabel: {
                    formatter: (value) =>
                        isOrdersQty
                            ? baseFormatters.number.plain.format(value)
                            : formatCurrency(value, currencyCode),
                },
            },
            grid: { containLabel: true, left: '3%', right: '4%', bottom: '15%' },
            series,
        };
    }, [baseFormatters.number.plain, chartData, formatCurrency, selectedChart, series, currencyCode]);

    const toggleChartView = useCallback(() => {
        setIsBarChart((prev) => !prev);
    }, []);

    const handleChartSelect = useCallback((event) => {
        setSelectedChart(event.target.value);
    }, []);

    return (
        <Card className="shadow-sm p-1">
            <Card.Header className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0">Sales Report</h5>

                <div className="d-flex gap-4 ms-auto">
                    <Button className="btn-custom-success" size="sm" onClick={toggleChartView}>
                        {isBarChart ? 'Detailed View' : 'Summary View'}
                    </Button>

                    <Form.Select
                        value={selectedChart}
                        onChange={handleChartSelect}
                        style={{ width: '150px' }}
                    >
                        {DASHBOARD_CHART_TYPES.map((item) => (
                            <option key={item.value} value={item.value}>
                                {item.label}
                            </option>
                        ))}
                    </Form.Select>
                </div>
            </Card.Header>

            <Card.Body style={{ minHeight: '320px' }}>
                {chartData.length ? (
                    <ReactECharts
                        option={option}
                        style={{ height: 350 }}
                        opts={{ renderer: 'svg' }}
                        notMerge
                        key={`${selectedChart}-${isBarChart ? 'bar' : 'line'}`}
                    />
                ) : (
                    <div className="text-center py-4">No Data</div>
                )}
            </Card.Body>
        </Card>
    );
};

export default memo(SalesChartContainer);